import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, Calendar, User, ArrowLeft, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const EmployeeDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employment_essentials');
  const [employmentSubTab, setEmploymentSubTab] = useState('contracts');
  const [performanceSubTab, setPerformanceSubTab] = useState('PIP');
  const [financialSubTab, setFinancialSubTab] = useState('payslips');
  const [trainingSubTab, setTrainingSubTab] = useState('training');
  const [documentsSubTab, setDocumentsSubTab] = useState('miscellaneous');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employee/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowViewDialog(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${doc.file_path}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const categories = [
    { id: 'employment_essentials', label: 'Employment Essentials', icon: User },
    { id: 'performance_conduct', label: 'Performance & Conduct', icon: FileText },
    { id: 'financial', label: 'Pay, Expenses & Financial', icon: FileText },
    { id: 'training_certification', label: 'Training & Certification', icon: FileText },
    { id: 'documents_uploads', label: 'Documents & Uploads', icon: FileText }
  ];

  const employmentCategories = [
    { id: 'contracts', label: 'Contract' },
    { id: 'handbook', label: 'Handbook' },
    { id: 'policies', label: 'Policies' },
    { id: 'new_starter_form', label: 'New Starter Form' },
    { id: 'emergency_contacts', label: 'Emergency Contacts' },
    { id: 'medical_allergies', label: 'Medical & Allergies' }
  ];

  const performanceCategories = [
    { id: 'PIP', label: 'PIP (Performance Improvement Plan)' },
    { id: 'hr_reports', label: 'HR Reports' },
    { id: 'rtw_forms', label: 'RTW Forms (Return to Work)' },
    { id: 'QFM', label: 'QFM' }
  ];

  const financialCategories = [
    { id: 'payslips', label: 'Payslips' },
    { id: 'expenses', label: 'Expenses & Mileage' },
    { id: 'wages_advance', label: 'Wages Advance' },
    { id: 'company_car_mileage', label: 'Company Car Mileage' }
  ];

  const trainingCategories = [
    { id: 'training', label: 'Training Certificates' }
  ];

  const documentsCategories = [
    { id: 'miscellaneous', label: 'Miscellaneous Uploads' },
    { id: 'other', label: 'Other Supporting Documents' }
  ];

  const getCategoryLabel = (category) => {
    const categoryMap = {
      'contracts': 'Contract',
      'handbook': 'Handbook',
      'policies': 'Policies',
      'new_starter_form': 'New Starter Form',
      'emergency_contacts': 'Emergency Contacts',
      'medical_allergies': 'Medical & Allergies',
      'PIP': 'PIP',
      'hr_reports': 'HR Reports',
      'rtw_forms': 'RTW Forms',
      'QFM': 'QFM',
      'payslips': 'Payslips',
      'expenses': 'Expenses & Mileage',
      'wages_advance': 'Wages Advance',
      'company_car_mileage': 'Company Car Mileage',
      'training': 'Training Certificates',
      'miscellaneous': 'Miscellaneous Uploads',
      'other': 'Other Supporting Documents',
      // Legacy support
      'Contract': 'Contract',
      'Handbook': 'Handbook',
      'Policies': 'Policies',
      'RTW Forms': 'RTW Forms',
      'HR Reports': 'HR Reports'
    };
    return categoryMap[category] || category;
  };

  const getFilteredDocuments = () => {
    if (activeTab === 'employment_essentials') {
      return documents.filter(doc => 
        doc.category === employmentSubTab || 
        (employmentSubTab === 'contracts' && doc.category === 'Contract') ||
        (employmentSubTab === 'handbook' && doc.category === 'Handbook') ||
        (employmentSubTab === 'policies' && doc.category === 'Policies')
      );
    } else if (activeTab === 'performance_conduct') {
      return documents.filter(doc => 
        doc.category === performanceSubTab ||
        (performanceSubTab === 'hr_reports' && doc.category === 'HR Reports') ||
        (performanceSubTab === 'rtw_forms' && doc.category === 'RTW Forms')
      );
    } else if (activeTab === 'financial') {
      return documents.filter(doc => 
        doc.category === financialSubTab ||
        (financialSubTab === 'payslips' && doc.category === 'Payslips')
      );
    } else if (activeTab === 'training_certification') {
      return documents.filter(doc => doc.category === trainingSubTab);
    } else if (activeTab === 'documents_uploads') {
      return documents.filter(doc => doc.category === documentsSubTab);
    }
    return documents.filter(doc => doc.category === activeTab);
  };

  const filteredDocuments = getFilteredDocuments();

  const getDocumentCount = (categoryId) => {
    if (categoryId === 'employment_essentials') {
      return documents.filter(d => 
        ['contracts', 'handbook', 'policies', 'new_starter_form', 'emergency_contacts', 'medical_allergies', 'Contract', 'Handbook', 'Policies'].includes(d.category)
      ).length;
    } else if (categoryId === 'performance_conduct') {
      return documents.filter(d => 
        ['PIP', 'hr_reports', 'rtw_forms', 'QFM', 'HR Reports', 'RTW Forms'].includes(d.category)
      ).length;
    } else if (categoryId === 'financial') {
      return documents.filter(d => 
        ['payslips', 'expenses', 'wages_advance', 'company_car_mileage', 'Payslips'].includes(d.category)
      ).length;
    } else if (categoryId === 'training_certification') {
      return documents.filter(d => d.category === 'training').length;
    } else if (categoryId === 'documents_uploads') {
      return documents.filter(d => ['miscellaneous', 'other'].includes(d.category)).length;
    }
    return documents.filter(d => d.category === categoryId).length;
  };

  const getSubCategoryCount = (subCatId) => {
    return documents.filter(d => 
      d.category === subCatId || 
      (subCatId === 'contracts' && d.category === 'Contract') || 
      (subCatId === 'handbook' && d.category === 'Handbook') || 
      (subCatId === 'policies' && d.category === 'Policies') ||
      (subCatId === 'hr_reports' && d.category === 'HR Reports') ||
      (subCatId === 'rtw_forms' && d.category === 'RTW Forms') ||
      (subCatId === 'payslips' && d.category === 'Payslips')
    ).length;
  };

  const getCurrentSubCategories = () => {
    switch (activeTab) {
      case 'employment_essentials': return employmentCategories;
      case 'performance_conduct': return performanceCategories;
      case 'financial': return financialCategories;
      case 'training_certification': return trainingCategories;
      case 'documents_uploads': return documentsCategories;
      default: return [];
    }
  };

  const getCurrentSubTab = () => {
    switch (activeTab) {
      case 'employment_essentials': return employmentSubTab;
      case 'performance_conduct': return performanceSubTab;
      case 'financial': return financialSubTab;
      case 'training_certification': return trainingSubTab;
      case 'documents_uploads': return documentsSubTab;
      default: return '';
    }
  };

  const setCurrentSubTab = (value) => {
    switch (activeTab) {
      case 'employment_essentials': setEmploymentSubTab(value); break;
      case 'performance_conduct': setPerformanceSubTab(value); break;
      case 'financial': setFinancialSubTab(value); break;
      case 'training_certification': setTrainingSubTab(value); break;
      case 'documents_uploads': setDocumentsSubTab(value); break;
      default: break;
    }
  };

  const handleCategoryChange = (categoryId) => {
    setActiveTab(categoryId);
    if (categoryId === 'employment_essentials') {
      setEmploymentSubTab('contracts');
    } else if (categoryId === 'performance_conduct') {
      setPerformanceSubTab('PIP');
    } else if (categoryId === 'financial') {
      setFinancialSubTab('payslips');
    } else if (categoryId === 'training_certification') {
      setTrainingSubTab('training');
    } else if (categoryId === 'documents_uploads') {
      setDocumentsSubTab('miscellaneous');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading documents...</div>
      </div>
    );
  }

  const currentSubCategories = getCurrentSubCategories();
  const currentSubTab = getCurrentSubTab();

  return (
    <div className="space-y-0 sm:space-y-6">
      {/* Header */}
      <div className="bg-white sm:rounded-xl shadow-sm border-b sm:border border-gray-200 p-4 sm:p-6">
        <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Documents</h1>
          <p className="text-sm text-gray-500 mt-1">Access and download your personal documents</p>
        </div>
      </div>

      {/* Mobile Category Selector */}
      <div className="sm:hidden bg-white shadow-sm p-4 space-y-3 border-b border-gray-200">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <div className="relative">
            <select
              value={activeTab}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label} ({getDocumentCount(category.id)})
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {currentSubCategories.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sub-category</label>
            <div className="relative">
              <select
                value={currentSubTab}
                onChange={(e) => setCurrentSubTab(e.target.value)}
                className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {currentSubCategories.map((subCat) => (
                  <option key={subCat.id} value={subCat.id}>
                    {subCat.label} ({getSubCategoryCount(subCat.id)})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Category Tabs (hidden on mobile) */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {categories.map((category) => {
            const count = getDocumentCount(category.id);
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === category.id
                    ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {category.label} {count > 0 && `(${count})`}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs for Desktop */}
        {currentSubCategories.length > 0 && (
          <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
            {currentSubCategories.map((subCat) => {
              const count = getSubCategoryCount(subCat.id);
              return (
                <button
                  key={subCat.id}
                  onClick={() => setCurrentSubTab(subCat.id)}
                  className={`flex-shrink-0 px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                    currentSubTab === subCat.id
                      ? 'text-emerald-600 font-medium bg-white border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {subCat.label} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Documents List */}
      <div className="bg-white sm:rounded-lg shadow-sm border-b sm:border border-gray-200">
        {filteredDocuments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No documents in this category</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 break-words">
                        {doc.file_name}
                      </h3>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1 break-words">
                          {doc.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <Calendar className="w-4 h-4" />
                          {new Date(doc.upload_date).toLocaleDateString()}
                        </span>
                        <span>{getCategoryLabel(doc.category)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <button
                      onClick={() => handleViewDocument(doc)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-gray-200 sm:border-0"
                      title="View document"
                    >
                      <Eye className="w-5 h-5" />
                      <span className="sm:hidden text-sm">View</span>
                    </button>
                    <button
                      onClick={() => handleDownloadDocument(doc)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-gray-200 sm:border-0"
                      title="Download document"
                    >
                      <Download className="w-5 h-5" />
                      <span className="sm:hidden text-sm">Download</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Document Dialog */}
      {showViewDialog && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold truncate pr-4">{selectedDocument.file_name}</h3>
              <button
                onClick={() => setShowViewDialog(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {selectedDocument.file_path.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${selectedDocument.file_path}`}
                  alt={selectedDocument.file_name}
                  className="max-w-full h-auto"
                />
              ) : selectedDocument.file_path.match(/\.pdf$/i) ? (
                <iframe
                  src={`${process.env.REACT_APP_BACKEND_URL}${selectedDocument.file_path}`}
                  className="w-full h-[600px] border-0"
                  title={selectedDocument.file_name}
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">
                    Preview not available for this file type
                  </p>
                  <button
                    onClick={() => handleDownloadDocument(selectedDocument)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    Download Document
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDocuments;
